import React from 'react';
import { Loader2, Stethoscope, UserRound, X } from 'lucide-react';

export default function AppointmentCheckInModal({
  isOpen,
  appointment,
  loading,
  submitting,
  availableChairs,
  availableDoctors,
  selectedChair,
  selectedDoctor,
  onSelectChair,
  onSelectDoctor,
  onClose,
  onSubmit,
}) {
  if (!isOpen) {
    return null;
  }

  const hasAvailableChair = availableChairs.some((item) => item.available);
  const hasAvailableDoctor = availableDoctors.some((item) => item.available);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.18)]">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">
              Check-in
            </p>
            <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900">
              Reserve chair and doctor
            </h2>
            <p className="mt-1.5 text-sm text-slate-500">
              {appointment?.patientName || 'Patient'}{appointment?.visitType ? ` | ${appointment.visitType}` : ''}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-white p-2.5 text-slate-500 transition hover:text-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-6">
          {loading ? (
            <div className="flex min-h-[220px] items-center justify-center rounded-[22px] border border-dashed border-slate-200 bg-slate-50 text-sm font-medium text-slate-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin text-blue-600" />
              Loading available chairs and doctors...
            </div>
          ) : (
            <>
              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                    <Stethoscope className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Available Chairs</h3>
                    <p className="text-sm text-slate-500">Choose one vacant chair for this consultation.</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {availableChairs.map((chair) => (
                    <button
                      key={chair.chairNumber}
                      type="button"
                      onClick={() => chair.available && onSelectChair(chair.chairNumber)}
                      disabled={!chair.available}
                      className={`rounded-[20px] border px-4 py-4 text-left transition ${
                        Number(selectedChair) === Number(chair.chairNumber)
                          ? 'border-blue-200 bg-blue-50 shadow-[0_10px_24px_rgba(37,99,235,0.08)]'
                          : chair.available
                          ? 'border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/60'
                          : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                      }`}
                    >
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                        Chair
                      </p>
                      <p className="mt-2 text-lg font-semibold text-slate-900">{chair.chairNumber}</p>
                      <p className="mt-1 text-xs font-medium">
                        {chair.available ? 'Available' : 'Occupied'}
                      </p>
                    </button>
                  ))}
                </div>

                {!hasAvailableChair ? (
                  <div className="rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
                    No chair is currently free. End the ongoing consultation or wait for a chair to become available.
                  </div>
                ) : null}
              </section>

              <section className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                    <UserRound className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Available Doctors</h3>
                    <p className="text-sm text-slate-500">
                      Only doctors or consultants marked available for today are selectable.
                    </p>
                  </div>
                </div>

                <div className="rounded-[22px] border border-slate-200 bg-slate-50/70 p-4">
                  <select
                    value={selectedDoctor}
                    onChange={(event) => onSelectDoctor(event.target.value)}
                    className="w-full rounded-[16px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-100"
                  >
                    <option value="">Select available doctor</option>
                    {availableDoctors.map((doctor) => (
                      <option
                        key={doctor.id}
                        value={doctor.name}
                        disabled={!doctor.available}
                      >
                        {doctor.name} | {doctor.role} | {doctor.startTime} - {doctor.endTime}
                        {!doctor.available ? ' | Unavailable' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {!hasAvailableDoctor ? (
                  <div className="rounded-[18px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
                    No doctor is currently available. Update today&apos;s availability before checking in this patient.
                  </div>
                ) : null}
              </section>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2.5 border-t border-slate-100 px-6 py-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={loading || submitting || !selectedChair || !selectedDoctor || !hasAvailableChair || !hasAvailableDoctor}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white shadow-[0_12px_24px_-18px_rgba(15,23,42,0.35)] transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
